import { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Button, Divider, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Box } from '/src/components';
import { useDateStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { DateRangeType } from '/src/lib/types';

export type DatePickerProps = {
  /**
   * Initial date range type. Defaults to `Yearly`
   */
  defaultRangeType?: DateRangeType;
};

export const DatePicker: React.FC<DatePickerProps> = observer(({ defaultRangeType = DateRangeType.Yearly }) => {
  /** Context **/
  const dateStore = useDateStore();

  /** Computed **/
  const rangeType = dateStore.rangeType;
  const dateRange = dateStore.dateRange;
  const currentDate = dateStore.currentDate;

  /** Methods **/

  /**
   * Sets the date range type
   */
  const setRangeType = (rangeType: DateRangeType) => {
    dateStore.setRangeType(rangeType);
    dateStore.calculateRange();
  };

  /**
   * Changes the current Transactions window
   */
  const changeDate = (modifier: number) => {
    dateStore.modifyCurrentDate(modifier);
    dateStore.calculateRange();
  };

  /** Effects **/

  useEffect(() => {
    setRangeType(defaultRangeType);
  }, []);

  /** Render **/
  return (
    <Box className="fadeDown">
      <Divider />
      <Box display="flex" layout-align="center center">
        <Box component="span" display="flex"></Box>

        <Box component="span" display="flex" layout-align="center center">
          <IconButton className="md-icon-button" onClick={() => changeDate(-1)}>
            <ChevronLeft className="md-icons material-icons" />
          </IconButton>

          {rangeType === DateRangeType.Weekly && (
            <Box component="span" className="thin textZoomIn center" flex="25">{`${dayjs(dateRange?.start).format(
              mediumDate
            )}-${dayjs(dateRange?.end).format(mediumDate)}`}</Box>
          )}
          {rangeType === DateRangeType.Monthly && (
            <Box component="span" className="thin textZoomIn center" flex="25">
              {dayjs(currentDate).format('MMMM YYYY')}
            </Box>
          )}
          {rangeType === DateRangeType.Yearly && (
            <Box component="span" className="thin textZoomIn center" flex="25">
              {dayjs(currentDate).format('YYYY')}
            </Box>
          )}

          <IconButton className="md-icon-button" onClick={() => changeDate(1)}>
            <ChevronRight className="md-icons material-icons" />
          </IconButton>
        </Box>

        <Box component="span" display="flex"></Box>

        <Box component="span" display="flex" justifyContent="center" alignItems="center">
          <Button
            className={`${rangeType === DateRangeType.Weekly && 'md-raised'}`}
            onClick={() => setRangeType(DateRangeType.Weekly)}
          >
            Weekly
          </Button>
          <Button
            className={`${rangeType === DateRangeType.Monthly && 'md-raised'}`}
            onClick={() => setRangeType(DateRangeType.Monthly)}
          >
            Monthly
          </Button>
          <Button
            className={`${rangeType === DateRangeType.Yearly && 'md-raised'}`}
            onClick={() => setRangeType(DateRangeType.Yearly)}
          >
            Yearly
          </Button>
        </Box>

        <Box component="span" display="flex"></Box>
      </Box>
    </Box>
  );
});
